import React from 'react'
import classnames from 'classnames'
import Switch from 'react-switch'

import { PreMakingTemplate } from '../../../core/pre-making'
import {
  preMakingContent,
  preMakingTemplateTableHeaders,
  preMakingWeeklyTemplateTableHeaders,
} from './pre-making-content'
import tableStyles from '../../styles/modules/table.module.scss'
import styles from '../../styles/modules/pre-making/pre-making-templates-table.module.scss'
import Button from '../button'

interface Props {
  templates: PreMakingTemplate[]
  templateType: 'weekly' | 'custom'
  isLoading: boolean
  onTemplateDetail: (template: PreMakingTemplate) => void
  onMakeTemplateActive?: (templateId: number) => void
  onMakeTemplateInactive?: (templateId: number) => void
  onCreateCustomTemplate?: () => void
}

const PreMakingTemplatesTable: React.FC<Props> = ({
  templates,
  templateType,
  isLoading,
  onTemplateDetail,
  onMakeTemplateActive,
  onMakeTemplateInactive,
  onCreateCustomTemplate,
}) => {
  const title =
    templateType === 'weekly'
      ? preMakingContent.weeklyTemplatesTitle
      : preMakingContent.customTemplatesTitle

  const noTemplatesMessage =
    templateType === 'weekly'
      ? preMakingContent.noWeeklyTemplates
      : preMakingContent.noCustomTemplates

  const filteredTemplates = templates.filter(
    template => template.templateType === templateType
  )

  const handleToggleActive = (
    template: PreMakingTemplate,
    checked: boolean
  ) => {
    if (!onMakeTemplateActive || !onMakeTemplateInactive) {
      return
    }

    const action = template.isActive ? '無効' : '有効'
    const confirmed = window.confirm(
      `テンプレート「${template.name}」を${action}にしてもよろしいですか？`
    )

    if (!confirmed) {
      return
    }

    if (template.isActive) {
      onMakeTemplateInactive(template.id)
    } else {
      onMakeTemplateActive(template.id)
    }
  }

  const tableHeaders =
    templateType === 'weekly'
      ? preMakingWeeklyTemplateTableHeaders
      : preMakingTemplateTableHeaders

  if (isLoading && filteredTemplates.length === 0) {
    return (
      <div className={styles.templateSection}>
        <h2 className={tableStyles.title}>{title}</h2>
        <div className={styles.loadingMessage}>
          {preMakingContent.loadingMessage}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.templateSection}>
      <h3 className={tableStyles.title}>{title}</h3>
      {templateType === 'custom' && onCreateCustomTemplate && (
        <div className={styles.createButtonContainer}>
          <Button
            title="新規作成"
            onClick={onCreateCustomTemplate}
            className={styles.createButton}
          />
        </div>
      )}
      {filteredTemplates.length > 0 ? (
        <div
          className={classnames(
            tableStyles.wrapper,
            tableStyles.zebra,
            tableStyles.stocker
          )}
        >
          <table className={tableStyles.container}>
            <tbody>
              <tr className={tableStyles.row}>
                {tableHeaders.map(header => (
                  <th key={header} className={tableStyles.cell}>
                    {header}
                  </th>
                ))}
              </tr>
              {filteredTemplates.map(template => (
                <tr key={template.id} className={tableStyles.row}>
                  <td className={tableStyles.cell}>{template.id}</td>
                  <td className={tableStyles.cell}>{template.name}</td>
                  <td className={tableStyles.cell}>{template.description}</td>
                  {templateType === 'weekly' && (
                    <td className={tableStyles.cell}>
                      <div className={tableStyles.switchCell}>
                        <Switch
                          onChange={checked =>
                            handleToggleActive(template, checked)
                          }
                          checked={template.isActive}
                        />
                      </div>
                    </td>
                  )}
                  <td className={tableStyles.buttonCell}>
                    <Button
                      title={preMakingContent.detailButtonTitle}
                      onClick={() => onTemplateDetail(template)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className={styles.noTemplates}>{noTemplatesMessage}</div>
        </>
      )}
    </div>
  )
}

export default PreMakingTemplatesTable
